import { Observable, concat, of, throwError } from "rxjs";
import { mergeMap, share, takeWhile } from "rxjs/operators";
import { createExperimentDefinition, } from "../../";
const LOCAL_DEV = false;
export class CloudExperimentRunner {
    constructor(devMode) {
        this.routes = new Map();
        this.connection = null;
        this.devMode = devMode;
        this.wssConnectUrl = devMode
            ? LOCAL_DEV
                ? "ws://127.0.0.1:9000/connect/"
                : "wss://cloud-staging.hash.ai/connect/"
            : "wss://cloud.hash.ai/connect/";
    }
    async handleRequest(req) {
        throw new Error("Sending single-runner commands to cloud is not supported!");
    }
    queueExperiment({ experimentName, project, manifestSrc, }) {
        const experimentSrc = JSON.parse(manifestSrc)["experimentsSrc"];
        const experimentArgs = createExperimentDefinition(experimentSrc, experimentName);
        const path = `${this.wssConnectUrl}?${new URLSearchParams({
            numSteps: (("maxSteps" in experimentArgs
                ? experimentArgs.maxSteps
                : experimentArgs.steps) ?? 0).toString(),
            experimentName,
            projectPath: project.path,
            projectRef: project.ref,
        })}`;
        const startedTime = Date.now();
        const notifiedSimulationIds = new Set();
        const stoppedSimIds = new Set();
        let created = false;
        let hasQueued = false;
        let hasErrored = false;
        const metricOutcomes = {};
        return new Observable((subscriber) => {
            if (created) {
                throw new Error("Can only subscribe once – call queueExperiment again");
            }
            created = true;
            try {
                this.connection = new WebSocket(path);
                this.connection.onmessage = (evt) => {
                    if (evt.data === "ping") {
                        return;
                    }
                    try {
                        const response = JSON.parse(evt.data);
                        if (response.error) {
                            hasErrored = true;
                            setTimeout(() => {
                                subscriber.error(new Error(response.error));
                            }, 250);
                        }
                        subscriber.next(response);
                    }
                    catch (err) {
                        subscriber.error(err);
                    }
                };
                this.connection.onclose = (evt) => {
                    if (this.devMode) {
                        console.warn("Closing websocket connecting with cloud because: ", evt);
                    }
                    if (!hasErrored) {
                        subscriber.complete();
                    }
                };
                this.connection.onerror = (evt) => {
                    subscriber.error(evt);
                };
            }
            catch (err) {
                subscriber.error(err);
            }
            return () => {
                this.connection?.close();
                this.connection = null;
            };
        }).pipe(share(), mergeMap((socketResponse) => {
            if (socketResponse.compute_usage_remaining === 0) {
                return concat(of(socketResponse), throwError(new Error("Out of cloud compute credits")));
            }
            return of(socketResponse);
        }), takeWhile((response) => hasErrored || response.running !== "finished", true), mergeMap((response) => {
            const responses = [];
            const experimentPlanEntries = Object.values(response.simulations)
                .filter((run) => !notifiedSimulationIds.has(run.id))
                .map((run) => {
                const newRun = {
                    fields: run.changed_properties,
                };
                return [run.id, newRun];
            });
            if (experimentPlanEntries.length > 0) {
                if (!hasQueued) {
                    hasQueued = true;
                    responses.push({
                        type: "queued",
                        experiment: {
                            experimentId: response.experimentId,
                            experimentName,
                            project,
                            status: "running",
                            definition: experimentArgs,
                            target: "cloud",
                            startedTime,
                            plan: {},
                            simulationIds: [],
                        },
                    });
                }
                responses.push({
                    type: "simulationsCreated",
                    plan: Object.fromEntries(experimentPlanEntries),
                });
                for (const [id] of experimentPlanEntries) {
                    notifiedSimulationIds.add(id);
                    responses.push({ simulationId: id, type: "started" });
                }
            }
            for (const status of Object.values(response.statuses ?? {})) {
                if (status.simulationRunId) {
                    if (typeof status.metricOutcome === "number") {
                        metricOutcomes[status.simulationRunId] = status.metricOutcome;
                    }
                    if (status.earlyStop &&
                        !stoppedSimIds.has(status.simulationRunId)) {
                        stoppedSimIds.add(status.simulationRunId);
                        responses.push({
                            type: "earlyStop",
                            status,
                            simulationId: status.simulationRunId,
                        });
                    }
                    if (status.stepsLink.analysisOutputs) {
                        responses.push({
                            simulationId: status.simulationRunId,
                            status: {
                                ...status,
                                metricOutcome: metricOutcomes[status.simulationRunId],
                            },
                            type: "analysis",
                            link: status.stepsLink.analysisOutputs,
                        });
                    }
                    if (status.stepsLink.agentSteps) {
                        responses.push({
                            simulationId: status.simulationRunId,
                            status,
                            type: "steps",
                        });
                    }
                    if (status.runnerError) {
                        const error = JSON.parse(JSON.stringify(status.runnerError));
                        responses.push({
                            simulationId: status.simulationRunId,
                            type: "error",
                            error,
                        });
                    }
                }
            }
            if (response.running === "stopping") {
                responses.push({ type: "stopping" });
            }
            return responses;
        }));
    }
}
//# sourceMappingURL=cloud-runner.js.map